import { useEffect, useState } from "react";
import { api } from "./api.ts";
import { syncAccountActivity } from "./activity.ts";
import { AnalyticsConsent, trackEvent } from "./analytics.tsx";
import { AuthPage } from "./AuthPage.tsx";
import {
  AboutPage,
  AccountPage,
  BlogArticlePage,
  BlogIndexPage,
  LegalPage,
  NotFoundPage,
  PricingPage,
} from "./ContentPages.tsx";
import { GamePage } from "./GamePage.tsx";
import { BoatGamePage } from "./BoatGamePage.tsx";
import { GameHubPage } from "./GameHubPage.tsx";
import { GreenhouseGamePage } from "./GreenhouseGamePage.tsx";
import { HomePage } from "./HomePage.tsx";
import { DiscoveryGamePage } from "./DiscoveryGamePage.tsx";
import { PremiumJourneyPage } from "./PremiumJourneyPage.tsx";
import { ReminderPage } from "./ReminderPage.tsx";
import type { Session } from "./Shell.tsx";

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  useEffect(() => {
    void api<{ session: Session | null }>("/api/auth/session/current")
      .then((result) => setSession(result.session))
      .catch(() => setSession(null))
      .finally(() => setSessionReady(true));
  }, []);
  useEffect(() => {
    if (!session) return;
    void syncAccountActivity().catch(() => {
      // Keep the local queue and retry on the next signed-in visit.
    });
  }, [session]);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("reminder") !== "1") return;
    void trackEvent("reminder_opened");
    url.searchParams.delete("reminder");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const path = window.location.pathname.replace(/\/$/, "") || "/";
  let page: React.ReactNode;
  if (path === "/") page = <HomePage session={session} />;
  else if (path === "/play") page = <GameHubPage session={session} />;
  else if (path === "/play/firefly") page = <GamePage session={session} />;
  else if (path === "/play/boat") page = <BoatGamePage session={session} />;
  else if (path === "/play/greenhouse") page = <GreenhouseGamePage session={session} />;
  else if (path === "/play/lighthouse") page = <PremiumJourneyPage game="lighthouse" session={session} />;
  else if (path === "/play/cloud-trail") page = <PremiumJourneyPage game="cloud-trail" session={session} />;
  else if (path === "/play/star-path") page = <PremiumJourneyPage game="star-path" session={session} />;
  else if (path === "/play/forest-post") page = <DiscoveryGamePage game="forest-post" session={session} />;
  else if (path === "/play/reef-lens") page = <DiscoveryGamePage game="reef-lens" session={session} />;
  else if (path === "/play/moon-harp") page = <DiscoveryGamePage game="moon-harp" session={session} />;
  else if (path === "/reminders") page = <ReminderPage session={session} />;
  else if (path === "/blog") page = <BlogIndexPage session={session} />;
  else if (path.startsWith("/blog/")) page = <BlogArticlePage session={session} slug={decodeURIComponent(path.slice("/blog/".length))} />;
  else if (path === "/pricing") page = <PricingPage session={session} />;
  else if (path === "/account") page = <AccountPage session={session} onSession={setSession} />;
  else if (path === "/about") page = <AboutPage session={session} />;
  else if (path === "/product-supplement") page = <LegalPage session={session} slug="product-supplement" />;
  else if (path.startsWith("/legal/")) page = <LegalPage session={session} slug={path.slice("/legal/".length)} />;
  else if (path === "/login") page = <AuthPage mode="login" session={session} onSession={setSession} />;
  else if (path === "/register") page = <AuthPage mode="register" session={session} onSession={setSession} />;
  else if (path === "/forgot-password") page = <AuthPage mode="forgot-password" session={session} onSession={setSession} />;
  else if (path === "/reset-password") page = <AuthPage mode="reset-password" session={session} onSession={setSession} />;
  else if (path === "/verify-email") page = <AuthPage mode="verify-email" session={session} onSession={setSession} />;
  else page = <NotFoundPage session={session} />;

  return <>{page}{sessionReady ? <AnalyticsConsent /> : null}</>;
}
